import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Stack, TextField, PrimaryButton, MessageBar, MessageBarType, Spinner, SpinnerSize, Label } from "@fluentui/react";
import { getModifyFunctionData, modifyFunction } from "../../../api/admin/api_admin";
import { ModifyFunctionResponse } from "../../../api/admin/models_admin";
import { AdminLabel, ErrorDialog, LinkButton } from "../components";
import { validateAuthorityLevel } from "../utils";

export const ModifyFunction = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const functionId = searchParams.get('function_id');
    
    const [data, setData] = useState<ModifyFunctionResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // Form states
    const [needAuthorityLevel, setNeedAuthorityLevel] = useState<string>("");

    useEffect(() => {
        if (functionId) {
            fetchData(functionId);
        }
    }, [functionId]);

    const fetchData = async (fid: string) => {
        setLoading(true);
        setError(null);
        try {
            const result = await getModifyFunctionData(fid);
            if ("error" in result) {
                setError(result.error);
            } else {
                setData(result);
                setNeedAuthorityLevel(result.function.FNC_need_authority_level.toString());
            }
        } catch (err) {
            setError("データの取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!functionId || !data) return;

        setSubmitting(true);
        setError(null);

        // バリデーション
        if (!needAuthorityLevel) {
            setError("必要権限レベルは必須です");
            setSubmitting(false);
            return;
        }
        if (!validateAuthorityLevel(needAuthorityLevel)) {
            setError("必要権限レベルは一桁の半角数字(1-9)のみ使用できます");
            setSubmitting(false);
            return;
        }

        try {
            const result = await modifyFunction({
                function_id: functionId,
                function_name: data.function.FNC_function_name,
                route_name: data.function.FNC_route_name,
                need_authority_level: needAuthorityLevel
            });

            if (result.success) {
                navigate("/menu/functions");
            } else {
                setError(result.message || "更新に失敗しました");
            }
        } catch (err) {
            setError("更新中にエラーが発生しました");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    if (!functionId || !data) {
        return (
            <Stack tokens={{ childrenGap: 20 }}>
                <MessageBar messageBarType={MessageBarType.error}>
                    {error ?? "機能IDが指定されていません"}
                </MessageBar>
                <LinkButton link_to="/menu/functions" text="戻る" />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="機能変更" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack tokens={{ childrenGap: 15 }} styles={{ root: { maxWidth: 600 } }}>
                <Stack>
                    <Label>機能ID</Label>
                    <div>{functionId}</div>
                </Stack>

                <Stack>
                    <Label>機能名</Label>
                    <div>{data.function.FNC_function_name}</div>
                </Stack>

                <TextField
                    name="need_authority_level"
                    label="必要権限レベル"
                    value={needAuthorityLevel}
                    onChange={(_, newValue) => setNeedAuthorityLevel(newValue || "")}
                    required
                    maxLength={1}
                    description="※使用できる文字は一桁の半角数字のみです。"
                />

                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <PrimaryButton
                        text="登録"
                        onClick={handleSubmit}
                        disabled={submitting || !needAuthorityLevel || !/^[1-9]$/.test(needAuthorityLevel)}
                    />
                    <LinkButton link_to="/menu/functions" text="戻る" />
                </Stack>
            </Stack>
        </Stack>
    );
}
